import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { z } from "zod";
import { Layout } from "@/components/Layout";
import { DisplayAd } from "@/components/DisplayAd";
import { PrizeBondChecker, DENOMINATIONS } from "@/components/prize-bond/PrizeBondChecker";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { getLatestPrizeBondDraws } from "@/lib/prize-bond.functions";
import { SITE_URL } from "@/lib/seo";

const searchSchema = z.object({
  d: z.coerce.number().int().optional(),
});

const FAQS: { q: string; a: string }[] = [
  { q: "Prize Bond online kaise check karein?", a: "Upar checker mein denomination chunein (jaise Rs. 750), bond number likhein aur 'Check Karain' dabayein. Result foran nazar aa jayega — data National Savings Pakistan ke published draw results se hai." },
  { q: "Rs. 750 ka pehla inaam kitna hai?", a: "Rs. 750 prize bond ka first prize Rs. 1,500,000 (15 lakh) hai. Second prize Rs. 500,000 (5 lakh) — 3 winners, aur third prize Rs. 9,300 — 1,696 winners hain." },
  { q: "Prize bond draw kab hota hai?", a: "Har bond ka draw quarterly hota hai — Rs. 100, 750, 7,500, 15,000, 25,000, 40,000 alag alag mahinon mein. Latest calendar #draw-schedule section mein diya hai." },
  { q: "Kya premium prize bonds regular se behtar hain?", a: "Premium bonds (Rs. 25,000 & Rs. 40,000) sirf registered form mein milte hain — koi bearer nahi. Prize amounts zyada aur profit tax-adjusted hoti hai. Detail #premium-vs-regular mein." },
  { q: "Prize claim karne ki last date kya hai?", a: "Draw ki date se 6 saal ke andar claim karna zaroori hai. Iske baad amount lapse ho jati hai. Documents aur process #how-to-claim mein detailed hai." },
  { q: "Kya prize bond par tax lagta hai?", a: "Haan — Filer ke liye 15%, Non-filer ke liye 30% withholding tax lagta hai jab prize amount Rs. 5,000 se zyada ho. FBR ke rules #tax-guide mein poore diye hain." },
  { q: "Prize bond kahan se kharidein?", a: "State Bank of Pakistan aur authorized commercial banks (HBL, MCB, UBL, Allied Bank, National Bank) se cash ya cheque par kharid sakte hain." },
  { q: "Bulk mein 100 numbers kaise check karein?", a: "Checker mein 'Bulk' tab pe click karein, har number nayi line par likhein ya comma se separate karein, aur 'Check Karain' dabayein. Ek dafa mein 100 tak numbers check ho sakte hain." },
  { q: "Range check kya hai?", a: "Agar aapke paas consecutive bonds hain (jaise 100001 se 100500 tak), Range tab use karein — poora range ek click mein check hoga (max 500 numbers)." },
  { q: "Prize bond drawing kis city mein hoti hai?", a: "Draws National Savings Pakistan ke centres mein hoti hain — Karachi, Lahore, Rawalpindi, Peshawar, Multan, Quetta, Muzaffarabad aur Faisalabad." },
  { q: "Kya main lost/stolen prize bond claim kar sakta hoon?", a: "Lost bond ka duplicate FIR aur newspaper advertisement ke baad 6 mahine mein issue ho sakta hai — State Bank branch se apply karein." },
  { q: "Non-filer prize bond kharid sakta hai?", a: "Haan, non-filer kharid sakta hai lekin withholding tax 30% hoga aur Rs. 40,000 premium bond sirf tax filers ke liye hai." },
  { q: "Prize bond ka color code kya hota hai?", a: "Rs. 100 — green, Rs. 200 — blue, Rs. 750 — orange, Rs. 1,500 — pink, Rs. 7,500 — purple, Rs. 15,000 — yellow, Rs. 25,000 — brown, Rs. 40,000 — red." },
  { q: "پرائز بانڈ آن لائن کیسے چیک کریں؟", a: "اوپر چیکر میں اپنا ڈینومینیشن منتخب کریں، بانڈ نمبر لکھیں اور 'Check Karain' دبائیں۔ نتیجہ فوراً نظر آئے گا۔ ڈیٹا نیشنل سیونگز پاکستان کے آفیشل ڈرا سے ہے۔" },
  { q: "قرعہ اندازی کہاں ہوتی ہے؟", a: "قرعہ اندازی مختلف شہروں میں ہوتی ہے — کراچی، لاہور، راولپنڈی، پشاور، ملتان، کوئٹہ، مظفرآباد اور فیصل آباد۔" },
  { q: "کیا میں لاسٹ بانڈ کلیم کر سکتا ہوں؟", a: "جی ہاں — FIR اور اخبار میں اشتہار کے بعد چھ ماہ میں ڈپلیکیٹ بانڈ جاری ہو سکتا ہے۔ سٹیٹ بینک کی برانچ سے رجوع کریں۔" },
  { q: "Prize bond vs bank FD — kaunsa behtar?", a: "Bank FD guaranteed return deta hai (jaise 15-20%), jabke prize bond ek lottery hai — hosakta hai bara prize milay ya kuch bhi nahi. Long-term wealth ke liye FD safer, entertainment/luck ke liye prize bond. Comparison #comparison mein." },
  { q: "Prize bond kitne saal tak valid hai?", a: "Bond khud lifetime valid hai — draws indefinitely hoti rahengi. Sirf winning claim ki 6-saal deadline hai per specific draw." },
  { q: "Draw list PDF kahan milta hai?", a: "Official PDFs savings.gov.pk par publish hoti hain draw ke 24 hours ke andar. Hamara data unhi PDFs se parse hota hai." },
  { q: "Bulk check secure hai?", a: "Haan — bond numbers server par process hote hain, save nahi hote. Koi personal data nahi liya jata, sirf number match karke result batate hain." },
];

const DENOM_META: Record<number, { title: string; h1: string; desc: string }> = {
  100: { title: "Rs. 100 Prize Bond Check 2026 — Latest Draw Result | DrHint", h1: "Rs. 100 Prize Bond Check Online", desc: "Rs. 100 prize bond ka latest draw check karein online. Bulk & range checker, official National Savings data upload-ready." },
  200: { title: "Rs. 200 Prize Bond Check 2026 — Latest Draw Result | DrHint", h1: "Rs. 200 Prize Bond Check Online", desc: "Rs. 200 prize bond ka latest draw result. Single, bulk aur range checker — free, National Savings Pakistan source." },
  750: { title: "Rs. 750 Prize Bond Check 2026 — Latest Draw Result | DrHint", h1: "Rs. 750 Prize Bond Check Online", desc: "Rs. 750 prize bond ka latest draw check karein — First prize Rs. 15 lakh. Bulk & range checker, official data upload-ready." },
  1500: { title: "Rs. 1,500 Prize Bond Check 2026 — Latest Draw Result | DrHint", h1: "Rs. 1,500 Prize Bond Check Online", desc: "Rs. 1,500 prize bond ka result — First prize Rs. 30 lakh. Free online checker, official data." },
  7500: { title: "Rs. 7,500 Prize Bond Check 2026 — Latest Draw Result | DrHint", h1: "Rs. 7,500 Prize Bond Check Online", desc: "Rs. 7,500 prize bond ka result — First prize Rs. 1.5 crore. Bulk aur range check ki suvidha, official source." },
  15000: { title: "Rs. 15,000 Prize Bond Check 2026 — Latest Draw Result | DrHint", h1: "Rs. 15,000 Prize Bond Check Online", desc: "Rs. 15,000 prize bond ka result — First prize Rs. 3 crore. Free online checker, official data upload-ready." },
  25000: { title: "Rs. 25,000 Premium Prize Bond Check 2026 | DrHint", h1: "Rs. 25,000 Premium Prize Bond Check Online", desc: "Premium Rs. 25,000 prize bond ka result — First prize Rs. 5 crore. Registered bonds ke liye online checker." },
  40000: { title: "Rs. 40,000 Premium Prize Bond Check 2026 | DrHint", h1: "Rs. 40,000 Premium Prize Bond Check Online", desc: "Premium Rs. 40,000 prize bond ka result — First prize Rs. 8 crore. Filer-only registered bonds ka checker." },
};

function pageMeta(d?: number) {
  if (d && DENOM_META[d]) return DENOM_META[d];
  return {
    title: "Prize Bond Check Online 2026 — Rs. 100 se Rs. 40,000 Tak | DrHint",
    h1: "Pakistan Prize Bond Checker",
    desc: "Pakistan Prize Bond check karein online — Rs. 100 se Rs. 40,000 tak. Single/bulk/range checker, official National Savings draw data ke liye upload-ready hub.",
  };
}

export const Route = createFileRoute("/prize-bond")({
  validateSearch: (input) => searchSchema.parse(input),
  loader: async () => {
    try {
      const res = await getLatestPrizeBondDraws();
      return { latest: res.ok ? res.items : [] };
    } catch {
      return { latest: [] as Array<{ denomination_id: number; draw_number: number; draw_date: string | null; draw_city: string | null; first_prize_amount: number | null }> };
    }
  },
  head: () => {
    const meta = pageMeta();
    const url = `${SITE_URL}/prize-bond`;
    const webApp = {
      "@context": "https://schema.org", "@type": "WebApplication",
      name: "Pakistan Prize Bond Checker", url, applicationCategory: "FinanceApplication",
      operatingSystem: "Web Browser", offers: { "@type": "Offer", price: "0", priceCurrency: "PKR" },
      description: meta.desc,
    };
    const faq = {
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    };
    const howTo = {
      "@context": "https://schema.org", "@type": "HowTo",
      name: "How to check Pakistan Prize Bond online",
      totalTime: "PT1M",
      step: [
        { "@type": "HowToStep", position: 1, name: "Denomination chunein", text: "Rs. 100 se Rs. 40,000 tak apna bond denomination select karein." },
        { "@type": "HowToStep", position: 2, name: "Mode select karein", text: "Single, Bulk (max 100) ya Range (max 500) mein se ek tab chunein." },
        { "@type": "HowToStep", position: 3, name: "Bond number likhein", text: "Apna bond number ya numbers input mein type karein — koi login nahi chahiye." },
        { "@type": "HowToStep", position: 4, name: "Check Karain dabayein", text: "Green button dabate hi result nazar aa jayega — jeete hue numbers highlight ho jayenge." },
        { "@type": "HowToStep", position: 5, name: "Prize claim karein", text: "Agar jeete hain to State Bank branch se 6 saal ke andar claim karein. Documents #how-to-claim mein." },
      ],
    };
    const breadcrumbs = {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Tools", item: `${SITE_URL}/tools` },
        { "@type": "ListItem", position: 3, name: "Prize Bond Checker", item: `${SITE_URL}/prize-bond` },
      ],
    };
    const dataset = {
      "@context": "https://schema.org", "@type": "Dataset",
      name: "Pakistan Prize Bond Winners Archive",
      description: "Pakistan National Savings prize bond draw results archive for all denominations as admin uploads official draw files.",
      url, license: "https://creativecommons.org/licenses/by/4.0/",
      creator: { "@type": "Organization", name: "DrHint" },
    };
    return {
      meta: [
        { title: meta.title },
        { name: "description", content: meta.desc },
        { property: "og:title", content: meta.title },
        { property: "og:description", content: meta.desc },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "en-PK", href: `${SITE_URL}/prize-bond` },
        { rel: "alternate", hrefLang: "ur-PK", href: `${SITE_URL}/prize-bond` },
        { rel: "alternate", hrefLang: "x-default", href: `${SITE_URL}/prize-bond` },
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(webApp) },
        { type: "application/ld+json", children: JSON.stringify(faq) },
        { type: "application/ld+json", children: JSON.stringify(howTo) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumbs) },
        { type: "application/ld+json", children: JSON.stringify(dataset) },
      ],
    };
  },
  component: PrizeBondHub,
});

function PrizeBondHub() {
  const { latest } = Route.useLoaderData();
  const search = Route.useSearch();
  const meta = pageMeta(search?.d);
  const initial = search?.d && DENOMINATIONS.some((x) => x.id === search.d) ? search.d : 750;

  useEffect(() => {
    if (typeof document !== "undefined" && search?.d) {
      document.title = meta.title;
    }
  }, [search?.d, meta.title]);

  return (
    <Layout>
      <article className="mx-auto max-w-5xl px-4 py-6 space-y-14">
        {/* 1. CHECKER */}
        <section id="checker" aria-labelledby="h1">
          <div className="mb-4">
            <div className="text-xs font-semibold uppercase tracking-widest text-emerald-600">Free · Official Data · No Signup</div>
            <h1 id="h1" className="mt-1 text-3xl md:text-4xl font-black tracking-tight">{meta.h1}</h1>
            <p className="mt-2 text-muted-foreground max-w-2xl">
              {meta.desc} Data source: <a rel="noopener nofollow" href="https://savings.gov.pk" className="text-emerald-600 underline">National Savings Pakistan</a>.
            </p>
          </div>
          <PrizeBondChecker initialDenomination={initial} />
        </section>

        <DisplayAd placement="mid-billboard" className="my-2" />

        {/* 2. LATEST RESULTS */}
        <section id="latest-results">
          <h2 className="text-2xl font-black mb-3">Latest Draws — Aakhri Nataij</h2>
          <p className="text-sm text-muted-foreground mb-3">Har denomination ka sabse recent draw — click karke apne numbers check karein.</p>
          {latest.length === 0 ? (
            <p className="text-sm text-muted-foreground">Draws abhi upload nahi hue — admin panel se official draw file add karte hi yahan latest results aa jayenge.</p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted text-xs uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">Denomination</th>
                    <th className="px-3 py-2 text-left">Draw #</th>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">City</th>
                    <th className="px-3 py-2 text-right">1st Prize</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {latest.slice(0, 8).map((r: {
                    denomination_id: number | null;
                    draw_number: number;
                    draw_date: string | null;
                    draw_city: string | null;
                    first_prize_amount: number | null;
                  }) => (
                    <tr key={`${r.denomination_id}-${r.draw_number}`}>
                      <td className="px-3 py-2 font-semibold">Rs. {(r.denomination_id ?? 0).toLocaleString("en-PK")}</td>
                      <td className="px-3 py-2">{r.draw_number}</td>
                      <td className="px-3 py-2">{r.draw_date ? <time dateTime={r.draw_date}>{r.draw_date}</time> : "—"}</td>
                      <td className="px-3 py-2">{r.draw_city ?? "—"}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.first_prize_amount ? `Rs. ${Number(r.first_prize_amount).toLocaleString("en-PK")}` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* 3. HOW TO CHECK */}
        <section id="how-to-check">
          <h2 className="text-2xl font-black mb-3">How to Check — Kaise Check Karein</h2>
          <ol className="grid md:grid-cols-5 gap-3 text-sm">
            {[
              { n: 1, t: "Denomination", d: "Rs. 100 se Rs. 40,000 tak ka bond chunein." },
              { n: 2, t: "Mode", d: "Single, Bulk ya Range tab select karein." },
              { n: 3, t: "Number", d: "Bond number ya numbers input mein daalein." },
              { n: 4, t: "Check", d: "Green 'Check Karain' button dabayein." },
              { n: 5, t: "Claim", d: "Jeetne par State Bank se 6 saal mein claim karein." },
            ].map((s) => (
              <li key={s.n} className="rounded-xl border border-border bg-card/50 p-4">
                <div className="text-2xl font-black text-emerald-600">{s.n}</div>
                <div className="font-semibold mt-1">{s.t}</div>
                <div className="text-xs text-muted-foreground mt-1">{s.d}</div>
              </li>
            ))}
          </ol>
        </section>

        {/* 4. DENOMINATION GUIDE */}
        <section id="denomination-guide">
          <h2 className="text-2xl font-black mb-3">Denomination Guide — Har Bond Ki Tafseel</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {DENOMINATIONS.map((d) => (
              <a key={d.id} href={`?d=${d.id}#checker`} className="rounded-xl border border-border bg-card/50 p-4 hover:border-emerald-500/50 transition">
                <div className={`inline-block rounded-full border px-2 py-0.5 text-xs font-bold ${d.color}`}>{d.label}</div>
                <div className="mt-2 font-semibold">Rs. {d.id.toLocaleString("en-PK")} Prize Bond</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {d.id >= 25000 ? "Premium registered bond — bearer nahi. Sirf filer ke liye (40k)." : d.id >= 7500 ? "High-value bearer bond — quarterly draw, bara jackpot." : "Low-denomination bearer bond — har koi kharid sakta hai."}
                </div>
                <div className="mt-2 text-xs font-semibold text-emerald-600">Check this denomination →</div>
              </a>
            ))}
          </div>
        </section>

        {/* 5. PRIZE STRUCTURE */}
        <section id="prize-structure">
          <h2 className="text-2xl font-black mb-3">Prize Structure — Inaam Ka Table</h2>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">Bond</th>
                  <th className="px-3 py-2 text-right">1st Prize</th>
                  <th className="px-3 py-2 text-right">2nd Prize (×3)</th>
                  <th className="px-3 py-2 text-right">3rd Prize</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border font-mono text-xs">
                <tr><td className="px-3 py-2 font-sans font-semibold">Rs. 100</td><td className="px-3 py-2 text-right">Rs. 700,000</td><td className="px-3 py-2 text-right">Rs. 200,000</td><td className="px-3 py-2 text-right">Rs. 1,000</td></tr>
                <tr><td className="px-3 py-2 font-sans font-semibold">Rs. 200</td><td className="px-3 py-2 text-right">Rs. 750,000</td><td className="px-3 py-2 text-right">Rs. 250,000</td><td className="px-3 py-2 text-right">Rs. 1,250</td></tr>
                <tr><td className="px-3 py-2 font-sans font-semibold">Rs. 750</td><td className="px-3 py-2 text-right">Rs. 1,500,000</td><td className="px-3 py-2 text-right">Rs. 500,000</td><td className="px-3 py-2 text-right">Rs. 9,300</td></tr>
                <tr><td className="px-3 py-2 font-sans font-semibold">Rs. 1,500</td><td className="px-3 py-2 text-right">Rs. 3,000,000</td><td className="px-3 py-2 text-right">Rs. 1,000,000</td><td className="px-3 py-2 text-right">Rs. 18,500</td></tr>
                <tr><td className="px-3 py-2 font-sans font-semibold">Rs. 7,500</td><td className="px-3 py-2 text-right">Rs. 15,000,000</td><td className="px-3 py-2 text-right">Rs. 5,000,000</td><td className="px-3 py-2 text-right">Rs. 93,000</td></tr>
                <tr><td className="px-3 py-2 font-sans font-semibold">Rs. 15,000</td><td className="px-3 py-2 text-right">Rs. 30,000,000</td><td className="px-3 py-2 text-right">Rs. 10,000,000</td><td className="px-3 py-2 text-right">Rs. 185,000</td></tr>
                <tr><td className="px-3 py-2 font-sans font-semibold">Rs. 25,000</td><td className="px-3 py-2 text-right">Rs. 50,000,000</td><td className="px-3 py-2 text-right">Rs. 15,000,000</td><td className="px-3 py-2 text-right">Rs. 312,000</td></tr>
                <tr><td className="px-3 py-2 font-sans font-semibold">Rs. 40,000</td><td className="px-3 py-2 text-right">Rs. 80,000,000</td><td className="px-3 py-2 text-right">Rs. 30,000,000</td><td className="px-3 py-2 text-right">Rs. 500,000</td></tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-2">Amounts indicative — official amounts har draw ke saath National Savings notification mein confirm hote hain.</p>
        </section>

        <DisplayAd placement="mid-billboard-2" sticky className="my-2" />

        {/* 6. DRAW SCHEDULE */}
        <section id="draw-schedule">
          <h2 className="text-2xl font-black mb-3">2026 Draw Schedule — Qura Andazi Calendar</h2>
          <p className="text-sm text-muted-foreground mb-3">Har prize bond ki draw quarterly hoti hai. Neeche approximate months diye hain — exact tareekh National Savings notification se confirm karein.</p>
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            {[
              { d: "Rs. 100", months: "Feb · May · Aug · Nov" },
              { d: "Rs. 200", months: "Mar · Jun · Sep · Dec" },
              { d: "Rs. 750", months: "Jan · Apr · Jul · Oct" },
              { d: "Rs. 1,500", months: "Feb · May · Aug · Nov" },
              { d: "Rs. 7,500", months: "Feb · May · Aug · Nov" },
              { d: "Rs. 15,000", months: "Jan · Apr · Jul · Oct" },
              { d: "Rs. 25,000 (Premium)", months: "Mar · Jun · Sep · Dec" },
              { d: "Rs. 40,000 (Premium)", months: "Mar · Jun · Sep · Dec" },
            ].map((s) => (
              <div key={s.d} className="rounded-lg border border-border bg-card/50 p-3">
                <div className="font-semibold">{s.d}</div>
                <div className="text-xs text-muted-foreground mt-1">{s.months}</div>
              </div>
            ))}
          </div>
        </section>

        {/* 7. HOW TO CLAIM */}
        <section id="how-to-claim">
          <h2 className="text-2xl font-black mb-3">Inaam Kaise Claim Karein — How to Claim</h2>
          <div className="rounded-xl border border-border bg-card/50 p-5 space-y-3 text-sm">
            <div><span className="font-semibold text-emerald-600">Documents:</span> Original prize bond, CNIC (aur photocopy), completed claim form (SBP-1) — filer status FBR portal se print karein.</div>
            <div><span className="font-semibold text-emerald-600">Timeline:</span> Draw ke 6 saal ke andar submit karna zaroori. Rs. 1,250 tak amount har authorized bank branch se milta hai; usse zyada National Savings centre ya SBP field office.</div>
            <div><span className="font-semibold text-emerald-600">Tax:</span> Rs. 5,000 se zyada prize par withholding tax deduct hota hai — Filer 15%, Non-filer 30%.</div>
            <div><span className="font-semibold text-emerald-600">Payment:</span> Amount aap ke bank account mein credit hoti hai (Rs. 500,000+) ya pay order form mein milti hai.</div>
          </div>
        </section>

        {/* 8. PREMIUM VS REGULAR */}
        <section id="premium-vs-regular">
          <h2 className="text-2xl font-black mb-3">Premium vs Regular — Kya Farq Hai?</h2>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div className="rounded-xl border border-border bg-card/50 p-4">
              <h3 className="font-bold text-lg">Regular (Bearer) Bonds</h3>
              <ul className="mt-2 list-disc pl-5 space-y-1 text-muted-foreground">
                <li>Denominations: 100, 200, 750, 1500, 7500, 15000</li>
                <li>Bearer form — jiske paas hai wohi malik</li>
                <li>Har koi kharid sakta hai</li>
                <li>Physical certificate</li>
                <li>Prize amounts relatively kam</li>
              </ul>
            </div>
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4">
              <h3 className="font-bold text-lg text-amber-600">Premium Registered Bonds ⭐</h3>
              <ul className="mt-2 list-disc pl-5 space-y-1 text-muted-foreground">
                <li>Denominations: 25,000 aur 40,000</li>
                <li>Registered — CNIC ke saath link</li>
                <li>Rs. 40k sirf tax filers ke liye</li>
                <li>Higher prize amounts (Rs. 8 crore tak)</li>
                <li>6-monthly profit bhi milta hai (~7% p.a.)</li>
              </ul>
            </div>
          </div>
        </section>

        {/* 9. COMPARISON */}
        <section id="comparison">
          <h2 className="text-2xl font-black mb-3">Prize Bond vs Savings Certificate vs Bank FD</h2>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted text-xs uppercase">
                <tr><th className="px-3 py-2 text-left">Feature</th><th className="px-3 py-2 text-left">Prize Bond</th><th className="px-3 py-2 text-left">Savings Cert.</th><th className="px-3 py-2 text-left">Bank FD</th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                <tr><td className="px-3 py-2 font-semibold">Return</td><td className="px-3 py-2">Lottery-based</td><td className="px-3 py-2">~17% (fixed)</td><td className="px-3 py-2">~15-20%</td></tr>
                <tr><td className="px-3 py-2 font-semibold">Guaranteed</td><td className="px-3 py-2 text-red-500">Nahi</td><td className="px-3 py-2 text-emerald-600">Haan</td><td className="px-3 py-2 text-emerald-600">Haan</td></tr>
                <tr><td className="px-3 py-2 font-semibold">Sharia-compliant</td><td className="px-3 py-2">Debate</td><td className="px-3 py-2">Nahi</td><td className="px-3 py-2">Nahi (unless Islamic)</td></tr>
                <tr><td className="px-3 py-2 font-semibold">Liquidity</td><td className="px-3 py-2">Anytime cash</td><td className="px-3 py-2">Penalty on early</td><td className="px-3 py-2">Penalty on early</td></tr>
                <tr><td className="px-3 py-2 font-semibold">Max Prize</td><td className="px-3 py-2">Rs. 8 crore</td><td className="px-3 py-2">N/A</td><td className="px-3 py-2">N/A</td></tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* 10. TAX GUIDE */}
        <section id="tax-guide">
          <h2 className="text-2xl font-black mb-3">Tax Guide — FBR Withholding Rules</h2>
          <div className="rounded-xl border border-border bg-card/50 p-5 text-sm space-y-2">
            <div><b>Section 156 Income Tax Ordinance:</b> Prize amount Rs. 5,000 se zyada par withholding tax deduct hota hai — source par hi kaat li jati hai.</div>
            <div className="grid md:grid-cols-2 gap-3 mt-3">
              <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-3"><div className="font-semibold text-emerald-600">Filer (Active Taxpayer)</div><div className="text-2xl font-black mt-1">15%</div><div className="text-xs text-muted-foreground mt-1">FBR ke Active Taxpayer List (ATL) mein hone par</div></div>
              <div className="rounded-lg border border-red-500/40 bg-red-500/5 p-3"><div className="font-semibold text-red-500">Non-Filer</div><div className="text-2xl font-black mt-1">30%</div><div className="text-xs text-muted-foreground mt-1">Double tax — filer banne se save karein</div></div>
            </div>
            <div className="mt-3 text-xs text-muted-foreground">Example: Rs. 1,500,000 prize par — Filer ko Rs. 1,275,000 milega (225k tax); Non-filer ko Rs. 1,050,000 (450k tax).</div>
          </div>
        </section>

        <DisplayAd placement="end-halfpage" className="my-2" />

        {/* 11. HISTORY */}
        <section id="history">
          <h2 className="text-2xl font-black mb-3">Prize Bond History in Pakistan</h2>
          <div className="prose prose-sm max-w-none text-muted-foreground space-y-3">
            <p>Pakistan mein prize bond scheme 1960 ke decade mein shuru hui — Government of Pakistan aur State Bank of Pakistan (SBP) ne mil kar National Savings ke through introduce ki. Maqsad tha ke aam awam mein saving ka culture badhaya jaye bina interest-based riba ke — lottery-based reward system ke zariye.</p>
            <p>Ibtida mein sirf Rs. 5 aur Rs. 10 ke bonds thay. Waqt ke saath denominations barhte gaye — 100, 200, 750, 1500, 7500, 15000. 2017 mein Premium Prize Bonds ka concept introduce hua — Rs. 25,000 aur Rs. 40,000 ke registered bonds jo bearer nahi hote, CNIC ke saath link hote hain.</p>
            <p>Draws National Savings ke authorized centres mein hoti hain — computerized machines ke zariye taake koi bhi manipulation na ho. Har draw ke result 24 hours ke andar official website (savings.gov.pk) par publish hote hain aur newspapers mein bhi print hote hain.</p>
            <p>Aaj Pakistan mein 3 crore se zyada prize bonds circulation mein hain — total value Rs. 900 arab se upar. Rs. 7,500 aur Rs. 15,000 sabse popular denominations hain kyunke un mein prize-to-investment ratio best hai.</p>
            <p>Recent policy change: 2020 mein FBR ne bearer bonds (Rs. 7,500, 15,000, 25,000, 40,000) ko phase out karne ka aelan kiya — un ki jagah premium (registered) bonds encourage kiye jaa rahe hain taake wealth tracking behtar ho sake.</p>
          </div>
        </section>

        {/* 12. GLOSSARY */}
        <section id="glossary">
          <h2 className="text-2xl font-black mb-3">Glossary — Zaroori Istelahat</h2>
          <dl className="grid md:grid-cols-2 gap-3 text-sm">
            <div className="rounded-lg border border-border p-3"><dt className="font-semibold">Draw</dt><dd className="text-muted-foreground text-xs mt-1">Quarterly qura andazi — computerized machine se winning numbers select karna.</dd></div>
            <div className="rounded-lg border border-border p-3"><dt className="font-semibold">Denomination</dt><dd className="text-muted-foreground text-xs mt-1">Bond ki face value — Rs. 100 se Rs. 40,000 tak.</dd></div>
            <div className="rounded-lg border border-border p-3"><dt className="font-semibold">Series</dt><dd className="text-muted-foreground text-xs mt-1">Bond number ka prefix jo issuance batch identify karta hai.</dd></div>
            <div className="rounded-lg border border-border p-3"><dt className="font-semibold">Bearer Bond</dt><dd className="text-muted-foreground text-xs mt-1">Physical certificate — jiske paas ho wo malik.</dd></div>
            <div className="rounded-lg border border-border p-3"><dt className="font-semibold">Registered Bond</dt><dd className="text-muted-foreground text-xs mt-1">CNIC ke saath link — sirf Rs. 25k & 40k premium.</dd></div>
            <div className="rounded-lg border border-border p-3"><dt className="font-semibold">Coupon</dt><dd className="text-muted-foreground text-xs mt-1">Premium bond ka 6-monthly profit payment.</dd></div>
          </dl>
        </section>

        {/* 13. URDU GUIDE */}
        <section id="urdu-guide" lang="ur" dir="rtl">
          <h2 className="text-2xl font-black mb-3">اردو گائیڈ — پرائز بانڈ کیسے چیک کریں</h2>
          <div className="prose prose-sm max-w-none text-muted-foreground space-y-3 text-right">
            <p>پاکستان کے پرائز بانڈ کا نتیجہ آن لائن چیک کرنا اب بہت آسان ہے۔ اوپر چیکر میں پہلے اپنا ڈینومینیشن منتخب کریں — روپے 100 سے روپے 40,000 تک۔ پھر اپنا بانڈ نمبر لکھیں اور "Check Karain" کا سبز بٹن دبائیں۔</p>
            <p>اگر آپ کے پاس ایک سے زیادہ بانڈز ہیں، تو Bulk ٹیب استعمال کریں — ایک وقت میں 100 نمبر تک چیک ہو سکتے ہیں۔ اگر آپ کے پاس مسلسل نمبروں کی سیریز ہے، تو Range ٹیب سے 500 نمبر ایک ساتھ چیک کریں۔</p>
            <p>یہ ڈیٹا سیدھا نیشنل سیونگز پاکستان کے آفیشل نتائج سے لیا جاتا ہے۔ کوئی رجسٹریشن یا سائن اپ کی ضرورت نہیں — بالکل مفت۔</p>
            <p>اگر آپ جیت جاتے ہیں، تو 6 سال کے اندر اسٹیٹ بینک کی برانچ سے انعام کلیم کریں۔ روپے 5,000 سے زیادہ کے انعام پر ودہولڈنگ ٹیکس (فائلر 15٪، نان فائلر 30٪) کاٹا جاتا ہے۔</p>
          </div>
        </section>

        {/* 14. ROMAN URDU */}
        <section id="roman-urdu-guide">
          <h2 className="text-2xl font-black mb-3">Roman Urdu Guide — Inaami Bond Ka Result</h2>
          <div className="prose prose-sm max-w-none text-muted-foreground space-y-2">
            <p>Pakistan ke inaami bond (prize bond) ka result online check karna ab bilkul asaan hai. Sabse pehle upar checker mein apna denomination chunein — Rs. 100 se le kar Rs. 40,000 tak. Phir apna bond number likhein aur green "Check Karain" button dabayein.</p>
            <p>Agar aap ke paas kai bonds hain, Bulk tab use karein — ek hi dafa mein 100 numbers tak check ho jate hain. Agar consecutive series hai, to Range tab se 500 tak check karein.</p>
            <p>Ye data seedha National Savings Pakistan ke official qura andazi (draw) results se aata hai — koi signup nahi, poori tarah free. Jeetne par State Bank ki nazdiki branch se 6 saal ke andar inaam claim karein.</p>
          </div>
        </section>

        {/* 15. FAQS */}
        <section id="faqs">
          <h2 className="text-2xl font-black mb-3">FAQs — Aksar Poochhe Jaane Wale Sawaal</h2>
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-left font-semibold">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* 16. RELATED TOOLS */}
        <section id="related-tools">
          {/* Intent-based deep links to denomination landing pages */}
          <div id="popular-searches" className="mb-10">
            <h2 className="text-2xl font-black mb-2">Log Ye Bhi Poochte Hain — Denomination-wise Result Pages</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Har denomination ka dedicated result page — draw date, prize breakdown, tax rules aur Hinglish
              step-by-step guide ke saath. Neeche wale sawaalat par click karein aur us specific bond ka poora
              detail page kholein.
            </p>
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              {[
                {
                  q: "Rs. 750 prize bond ka aaj ka result kahan milega?",
                  a: "Rs. 750 ka latest draw list, first prize Rs. 15 lakh ki breakdown aur claim process — sab yahan.",
                  href: "/prize-bond-list-750-today",
                  cta: "750 prize bond list today dekhein",
                },
                {
                  q: "Rs. 100 wala prize bond kaise check karein online?",
                  a: "Rs. 100 ke sabse chhote bond ka draw schedule, prize table aur Hinglish guide.",
                  href: "/prize-bond-100-result-today",
                  cta: "100 prize bond result today kholein",
                },
                {
                  q: "200 wale prize bond ki list kaise nikalti hai?",
                  a: "Rs. 200 bond ka draw calendar, winning numbers verify karne ka tareeqa aur tax rules.",
                  href: "/prize-bond-200-result-today",
                  cta: "200 wala prize bond page dekhein",
                },
                {
                  q: "Rs. 1500 prize bond mein first prize kitna milta hai?",
                  a: "Rs. 1,500 bond ka Rs. 30 lakh first prize, second/third structure aur claim deadline.",
                  href: "/prize-bond-1500-result-today",
                  cta: "1500 prize bond result kholein",
                },
                {
                  q: "25000 premium prize bond aur regular mein farq kya hai?",
                  a: "Registered premium bond, 6-monthly profit, filer-friendly rules aur Rs. 5 crore top prize.",
                  href: "/prize-bond-25000-result-today",
                  cta: "25000 prize bond result dekhein",
                },
                {
                  q: "40000 wale prize bond ka draw kab hota hai?",
                  a: "Filer-only Rs. 40,000 premium bond — Rs. 8 crore first prize aur quarterly schedule.",
                  href: "/prize-bond-40000-result-today",
                  cta: "40000 prize bond result kholein",
                },
              ].map((row) => (
                <a
                  key={row.href}
                  href={row.href}
                  className="rounded-xl border border-border bg-card/50 p-4 hover:border-emerald-500/60 transition block"
                >
                  <div className="font-semibold text-foreground">{row.q}</div>
                  <div className="text-xs text-muted-foreground mt-1">{row.a}</div>
                  <div className="mt-2 text-xs font-semibold text-emerald-600">{row.cta} →</div>
                </a>
              ))}
            </div>
          </div>

          <h2 className="text-2xl font-black mb-3">Related Tools — DrHint Par Aur Kya Milega?</h2>
          <div className="grid md:grid-cols-3 gap-3 text-sm">
            <a href="/pakistan" className="rounded-xl border border-border bg-card/50 p-4 hover:border-emerald-500/50">
              <div className="font-semibold">🥇 Gold Rate Pakistan</div><div className="text-xs text-muted-foreground mt-1">Live 24k/22k gold price</div>
            </a>
            <a href="/silver-rate-pakistan" className="rounded-xl border border-border bg-card/50 p-4 hover:border-emerald-500/50">
              <div className="font-semibold">🥈 Silver Rate Pakistan</div><div className="text-xs text-muted-foreground mt-1">Chandi ka aaj ka bhao</div>
            </a>
          </div>
        </section>

        {/* 17. DISCLAIMER */}
        <section id="disclaimer">
          <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 text-sm text-muted-foreground">
            <b className="text-amber-600">Disclaimer:</b> Data National Savings Pakistan (savings.gov.pk) ke public draw results se parse hota hai. Prize claim se pehle official source se hamesha verify karein. DrHint kisi bhi loss ya galat information ke liye zimmedaar nahi hai. Ye "guaranteed win" ya gambling scheme nahi — prize bond ek government-approved saving instrument hai. Amounts, tax rates aur schedules time ke saath badal sakte hain.
          </div>
        </section>
      </article>
    </Layout>
  );
}